import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { Buffer } from 'buffer';
import { Button } from './button';
import { captureException } from '@/utils/sentry';
import { ListItemSeparator } from './list-item';
import { PrinterSelectList } from './printer-select-list';
import { StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native';
import { TagPrintingBluetoothPermissions } from './tag-printing-bluetooth-permissions';
import { TYPOGRAPHY } from '@/styles';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useBluetoothContext } from '@/contexts/bluetooth-context';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  buttonText: string;
  onClose: () => void;
  onPrint: () => void;
  renderTemplate: () => Promise<string>;
  style?: StyleProp<ViewStyle>;
}

export const PrintLabelConfirmation: React.FC<Props> = ({
  buttonText,
  onClose,
  onPrint,
  renderTemplate,
  style,
}) => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { hasPermission, selectedDevice } = useBluetoothContext();
  const { showErrorToast } = useToasts();

  const [isDeviceConnected, setIsDeviceConnected] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const readyToPrint = selectedDevice && isDeviceConnected;

  useEffect(() => {
    const checkConnectedDevice = async () => {
      if (selectedDevice) {
        const isConnected = await selectedDevice.isConnected();
        if (!isConnected) {
          showErrorToast(i18n.t('bluetooth_device.disconnected'));
        }

        setIsDeviceConnected(isConnected);
      }
    };

    checkConnectedDevice();
  }, [selectedDevice, showErrorToast]);

  const onSubmit = useCallback(async () => {
    if (!readyToPrint) {
      return;
    }

    setLoadingTemplate(true);

    try {
      const renderedTemplate = await renderTemplate();

      if (renderedTemplate) {
        const printBuffer = Buffer.from(renderedTemplate).toString('base64');
        await selectedDevice.write(printBuffer, 'base64');
        onPrint();
        onClose();
      } else {
        throw new Error('No template rendered');
      }
    } catch (e) {
      captureException(e);
      showErrorToast();
    }

    setLoadingTemplate(false);
  }, [
    onClose,
    onPrint,
    readyToPrint,
    renderTemplate,
    selectedDevice,
    showErrorToast,
  ]);

  return (
    <View style={style}>
      {!hasPermission ? (
        <TagPrintingBluetoothPermissions goBack={onClose} />
      ) : (
        <>
          <View style={styles.header}>
            <Text style={TYPOGRAPHY.navigationTitle}>
              {i18n.t('select_printer')}
            </Text>
            <Text style={[TYPOGRAPHY.p, styles.messageText]}>
              {i18n.t('select_printer.message')}
            </Text>
          </View>
          <PrinterSelectList
            containerStyle={styles.printerSelect}
            ItemSeparatorComponent={() => (
              <ListItemSeparator style={styles.printerListSeparator} />
            )}
            listItemStyle={styles.printerSelectListItem}
          />
          <Button
            disabled={!readyToPrint}
            loading={loadingTemplate}
            onPress={onSubmit}
            trackingData={{
              name: 'print_label_confirmation_button',
              properties: {
                deviceName: selectedDevice?.name,
                restaurantUUID,
                supplierUUID,
              },
            }}
          >
            {buttonText}
          </Button>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    gap: 4,
  },
  messageText: {
    lineHeight: 22,
  },
  printerListSeparator: {
    marginHorizontal: 0,
  },
  printerSelect: {
    marginVertical: 16,
  },
  printerSelectListItem: {
    paddingHorizontal: 0,
  },
});
